/**
 * «За зоной»: разбор полигонов зон катания и вердикт по точке.
 *
 * ПЕРЕНЕСЕНО ИЗ `Aida-PWA/src/features/map/zones/` (wkt.ts и ride-половина
 * zone-status.ts), сознательно и почти без изменений. Там это уже прошло
 * ревью и сверено с SQL; переписывать заново значило бы завести второй
 * ответ на тот же вопрос.
 *
 * Взята только ride-половина. Парковочные зоны здесь не нужны (заказчик
 * выбрал зону катания), а вместе с ними отпадает и разбор точечных зон:
 * у RideZone нет ни центра, ни радиуса — только polygon_wkt
 * (`api/public/v1/public.proto`, message RideZone).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ЭТО ЗЕРКАЛО СЕРВЕРНОГО ЗАПРОСА, И В ЭТОМ ВЕСЬ СМЫСЛ
 * ─────────────────────────────────────────────────────────────────────────
 * Повторяет `IsPointInRideZone` — `fleet/internal/data/ride_zone_repo.go:219-255`.
 * Расхождение с ним хуже, чем отсутствие признака: скаут поедет по списку,
 * который никто не сможет воспроизвести.
 *
 * Считаем на клиенте не от лени: HTTP-ручки «точка в зоне» не существует,
 * `IsPointInRideZone` — внутренний gRPC. Полигоны приложение и так грузит,
 * чтобы их нарисовать.
 */
import { wktToGeoJSON } from '@terraformer/wkt'
import { booleanPointInPolygon } from '@turf/boolean-point-in-polygon'
import type * as GeoJSON from 'geojson'

/**
 * `true` — точка на границе считается СНАРУЖИ.
 *
 * Это контракт с `ST_Contains`, а не настройка. По определению OGC попадание
 * требует пересечения с внутренностью полигона, поэтому точка ровно на линии
 * НЕ содержится. Turf по умолчанию отвечает наоборот. Оставленное умолчание
 * развело бы клиент и базу ровно там, где стоит велосипед.
 */
const BOUNDARY_SEMANTICS = { ignoreBoundary: true } as const

/** Зона катания с провода. */
export type RideZone = {
  id: string
  name: string
  /** allowed | forbidden */
  kind: string
  polygonWkt: string
}

/**
 * Полигон зоны как GeoJSON, либо null — «пропустить эту строку».
 *
 * ОДНА БИТАЯ СТРОКА НЕ ДОЛЖНА ГАСИТЬ ВЕСЬ СЛОЙ. Зоны приходят списком, и
 * исключение на четвёртой унесло бы с собой все остальные.
 *
 * Разбор — библиотекой, и это не вкусовщина. Ручной парсер обязан пережить
 * внутренние кольца (дырка, попавшая в границу, делает стоящего в ней
 * «внутри зоны»), EMPTY-геометрию, экспоненциальную запись и разнобой пробелов.
 */
export function parseZonePolygon(
  wkt: string | undefined,
): GeoJSON.Polygon | GeoJSON.MultiPolygon | null {
  if (!wkt) return null

  let geometry: GeoJSON.Geometry
  try {
    geometry = wktToGeoJSON(wkt) as GeoJSON.Geometry
  } catch {
    return null
  }

  if (geometry.type === 'MultiPolygon') {
    const usable = geometry.coordinates.filter(
      (rings) => rings.length > 0 && rings.every(isUsableRing),
    )
    if (usable.length === 0) return null
    return { type: 'MultiPolygon', coordinates: usable }
  }

  if (geometry.type !== 'Polygon') return null
  if (geometry.coordinates.length === 0) return null
  if (!geometry.coordinates.every(isUsableRing)) return null
  return geometry
}

/**
 * RFC 7946 §3.1.6: кольцо замкнуто и содержит не меньше четырёх точек.
 *
 * «Разобралось» — не контракт. `POLYGON EMPTY` даёт полигон без колец, а
 * незамкнутое кольцо роняет turf изнутри вердикта — на слой ниже, где
 * искать причину пустой карты никто не станет.
 */
function isUsableRing(ring: GeoJSON.Position[]): boolean {
  if (ring.length < 4) return false
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (!first || !last) return false
  return first[0] === last[0] && first[1] === last[1]
}

function contains(zone: RideZone, point: [number, number]): boolean {
  const polygon = parseZonePolygon(zone.polygonWkt)
  if (polygon === null) return false
  return booleanPointInPolygon(point, polygon, BOUNDARY_SEMANTICS)
}

/**
 * Почему точка вне зоны. Значения — серверные (`violationKind`), не свои:
 * так объяснение клиента и сервера не разъедутся.
 */
export type ZoneViolation = 'forbidden' | 'outside_allowed'

/**
 * Вердикт по одной точке.
 *
 * `'unknown'` — третье состояние, а не синоним «нарушений нет». Зон нет в
 * кэше — мы не знаем; выдавать за «всё в порядке» нельзя, иначе список
 * «за зоной» будет пуст именно тогда, когда данные не доехали.
 */
export type ZoneVerdict =
  | { out: false }
  | { out: true; violation: ZoneViolation }
  | { out: 'unknown' }

/**
 * ```
 * out = ∃ z : z.kind === 'forbidden' ∧ contains(z, p)
 *     ∨ (∃ z : z.kind === 'allowed')
 *       ∧ ¬(∃ z : z.kind === 'allowed' ∧ contains(z, p))
 * ```
 *
 * ⚠ Второе слагаемое выглядит избыточным и таковым не является.
 * ОТСУТСТВУЮЩИЙ белый список означает «ограничения на область нет», а
 * присутствующий — что всё вне его нарушение. Схлопывание этих случаев в день,
 * когда таблица ride_zones пуста, объявит нарушителями все байки в городе.
 *
 * Проверка «белый список существует» СЧИТАЕТ и те зоны, чей полигон не
 * разобрался. Ответить на неё через геометрию — значит превратить битую строку
 * в разрешение, а это единственное направление, в котором ошибаться нельзя.
 */
export function rideZoneVerdict(
  point: { lat: number; lng: number } | null,
  zones: readonly RideZone[] | undefined,
): ZoneVerdict {
  if (point === null) return { out: 'unknown' }
  if (zones === undefined || zones.length === 0) return { out: 'unknown' }

  // Единственное место, где lat/lng становится парой. И GeoJSON, и turf —
  // [lng, lat], перепутать их значит искать байки в Сомали.
  const here: [number, number] = [point.lng, point.lat]

  if (zones.some((z) => z.kind === 'forbidden' && contains(z, here))) {
    return { out: true, violation: 'forbidden' }
  }

  const allowed = zones.filter((z) => z.kind === 'allowed')
  if (allowed.length === 0) return { out: false }
  if (allowed.some((z) => contains(z, here))) return { out: false }

  return { out: true, violation: 'outside_allowed' }
}

/** Короткий предикат для списков. `unknown` — НЕ нарушение. */
export function isOutOfRideZone(
  point: { lat: number; lng: number } | null,
  zones: readonly RideZone[] | undefined,
): boolean {
  return rideZoneVerdict(point, zones).out === true
}

/**
 * «За зоной» в смысле ТЗ маршрутов сбора: точка не внутри ни одной
 * РАЗРЕШЁННОЙ зоны. Запрещённые сюда не входят — велик в запрещённом
 * дворе посреди города в черте катания, его не везут «вернуть в зону».
 *
 * Зон нет в кэше или разрешённых нет вовсе — `false`: незнание и
 * «ограничений нет» — не повод отправлять за великом машину.
 */
export function isOutsideRidingArea(
  point: { lat: number; lng: number },
  zones: readonly RideZone[] | undefined,
): boolean {
  const allowed = (zones ?? []).filter((z) => z.kind === 'allowed')
  if (allowed.length === 0) return false
  const here: [number, number] = [point.lng, point.lat]
  return !allowed.some((z) => contains(z, here))
}

/**
 * Метры от точки до ближайшей РАЗРЕШЁННОЙ зоны катания; 0 — внутри.
 *
 * Нужен фильтру карты «вне зоны далеко / рядом»: велик у самой границы скаут
 * закатывает руками, за велик в девяти километрах едет машина, и это две
 * разные работы. Сервер метры знает (`problemDetail`), но только когда C1
 * выкачен и только текстом — считать по тем же полигонам, что уже на
 * экране, надёжнее, чем разбирать фразу.
 *
 * Запрещённые зоны в счёт не идут: их граница — не то место, куда катят
 * велик. Сам запрет отвечает `rideZoneVerdict`.
 *
 * `null` — не знаем (зон нет в кэше или ни один полигон не разобрался), и
 * это не «рядом»: фильтр по нулю метров отнёс бы весь город к «рядом».
 * Разрешённых зон нет вовсе — 0, как и в вердикте: нет белого списка — нет
 * и «снаружи».
 */
export function distanceOutsideM(
  point: { lat: number; lng: number },
  zones: readonly RideZone[] | undefined,
): number | null {
  if (zones === undefined || zones.length === 0) return null
  const allowed = zones.filter((z) => z.kind === 'allowed')
  if (allowed.length === 0) return 0

  const here: [number, number] = [point.lng, point.lat]
  let best = Number.POSITIVE_INFINITY
  for (const z of allowed) {
    const polygon = parseZonePolygon(z.polygonWkt)
    if (polygon === null) continue
    if (booleanPointInPolygon(here, polygon, BOUNDARY_SEMANTICS)) return 0
    const polygons = polygon.type === 'Polygon' ? [polygon.coordinates] : polygon.coordinates
    for (const rings of polygons) {
      for (const ring of rings) {
        for (let i = 0; i + 1 < ring.length; i += 1) {
          best = Math.min(best, segmentDistanceM(point, ring[i]!, ring[i + 1]!))
        }
      }
    }
  }
  return Number.isFinite(best) ? best : null
}

/**
 * Метры от точки до отрезка границы.
 *
 * Плоская проекция вокруг самой точки, как у `circlePolygon` в `lib/geo.ts`:
 * метр долготы короче метра широты в cos(широта) раз, кривизной Земли на
 * городских расстояниях пренебрегаем.
 */
function segmentDistanceM(
  p: { lat: number; lng: number },
  a: GeoJSON.Position,
  b: GeoJSON.Position,
): number {
  const kx = 111_320 * Math.cos((p.lat * Math.PI) / 180)
  const ky = 110_574
  const ax = (a[0]! - p.lng) * kx
  const ay = (a[1]! - p.lat) * ky
  const dx = (b[0]! - a[0]!) * kx
  const dy = (b[1]! - a[1]!) * ky
  const len2 = dx * dx + dy * dy
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2))
  return Math.hypot(ax + t * dx, ay + t * dy)
}

export const zoneViolationLabel: Record<ZoneViolation, string> = {
  forbidden: 'В запрещённой зоне',
  outside_allowed: 'Вне зоны катания',
}
